import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomFilterSelect from '../../shared/customFilterSelect';
import HatIcon from '../../assets/icons/hatButton.svg';
import ArrowGreyIcon from '../../assets/icons/arrowGrey.svg';
import ArrowBlueIcon from '../../assets/icons/arrowBlue.svg';
import DownloadSchoolsReport from '../../components/adminSchools/downloadSchoolsReport';
import DateRangeFilter from '../../components/dateRangeFilter';
import AdminProcessScopeFilter from '../../components/adminProcessScopeFilter';
import SchoolItem from '../../components/adminSchools/schoolItem';
import BreadCrumb from '../../components/breadCrumb';
import ModalContainer from '../../components/modalContainer';
import CreateSchool from '../../components/adminSchools/createSchool';
import EditSchoolLookupModal from '../../components/adminSchools/editSchoolLookupModal';
import {
  getControlSocialActivities,
  getMunicipalities,
  getSchools,
  getUsers
} from '../../API/apiService';
import {
  ADMIN_SCOPE_VALUES,
  buildScopedSchools,
  filterConfirmedSchools,
  normalizeAdminScope
} from '../../utils/adminScope';
import { GROUP_STATUS, resolveGroupStatus } from '../../utils/groupStatus';
import {
  getDateFiltersFromSearchParams,
  getGroupFinalizedDate,
  getGroupStartDate,
  getRecordCreatedDate,
  isDateInRange
} from '../../utils/dateFilters';
import {
  ENTITY_TYPE_FILTER_OPTIONS,
  getEntityTypeFilterLabel,
  matchesEntityType
} from '../../utils/entityType';
import './style.scss';

const ALL_FILTER_ID = '__all__';
const DATE_FIELD_START = 'start';
const DATE_FIELD_FINALIZATION = 'finalization';
const STATUS_FILTER_OPTIONS = [
  { id: GROUP_STATUS.NOT_STARTED, name: GROUP_STATUS.NOT_STARTED },
  { id: GROUP_STATUS.IN_PROGRESS, name: GROUP_STATUS.IN_PROGRESS },
  { id: GROUP_STATUS.FINISHED, name: GROUP_STATUS.FINISHED },
  { id: GROUP_STATUS.CANCELLED, name: GROUP_STATUS.CANCELLED }
];

const defaultItemsDescription = [
  { name: 'Cod. de Proceso Formativo', code: 'CDE', value: 1 },
  { name: 'Tipo de Proceso Formativo', code: 'TDE', value: 2 },
  { name: 'Grupo', code: 'GRP', value: 0 },
  { name: 'Estado', code: 'EST', value: 10 },
  { name: 'Fecha de finalización', code: 'FIF', value: 11 },
  { name: 'Mediador', code: 'DMZ', value: 3 },
  { name: 'Comuna', code: 'CO', value: 4 },
  { name: 'Barrio', code: 'BAR', value: 5 },
  { name: 'Organizacion Madrina', code: 'OM', value: 7 },
  { name: 'Nombre Entidad Aliada', code: 'ANO', value: 8 },
  { name: 'Acciones', code: 'ACC', value: 9 }
];

const copItemsDescription = [
  { name: 'Cod. de Asesoría', code: 'CDE', value: 1 },
  { name: 'Tipo de Asesoría', code: 'TDE', value: 2 },
  { name: 'Mediador', code: 'DMZ', value: 3 },
  { name: 'Mediador(es) adicional(es)', code: 'MAD', value: 10 },
  { name: 'Comuna', code: 'CO', value: 4 },
  { name: 'Barrio', code: 'BAR', value: 5 },
  { name: 'Dependencia', code: 'OM', value: 7 },
  { name: 'Nombre Asesoría', code: 'ANO', value: 8 },
  { name: 'Acciones', code: 'ACC', value: 9 }
];

const schoolType = [
  { name: 'INSTITUCION EDUCATIVA', id: 'IE' },
  { name: 'DIPLOMADO', id: 'DIPLOMADO' },
  { name: 'JAL', id: 'JAL' },
  { name: 'JAC', id: 'JAC' },
  { name: 'ORGANIZACIONES SOCIALES', id: 'OS' },
  { name: 'COLECTIVO', id: 'COLECTIVO' }
];

const COP_ADVISORY_TYPE_LABELS = {
  DEPEN: 'Asesoría',
  ASESORIA: 'Asesoría',
  COMITE_PEDAGOGICO: 'Comité Pedagógico',
  EVENTO: 'Evento',
  OTRO: 'Otro'
};

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function sortOptionsByName(options) {
  return [...options].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'es', {
      sensitivity: 'base'
    })
  );
}

function getOptionLabel(options, selectedId) {
  if (!selectedId) return '';
  return options.find(option => String(option.id) === String(selectedId))?.name || '';
}

function parseMultiFilterValue(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return [];

  try {
    const parsed = JSON.parse(normalized);
    if (Array.isArray(parsed)) {
      return parsed.map(item => String(item).trim()).filter(Boolean);
    }
  } catch (_error) {
    // Compatibilidad con enlaces anteriores que guardaban un solo valor.
  }

  return [normalized];
}

function serializeMultiFilterValue(values) {
  return Array.isArray(values) && values.length > 0
    ? JSON.stringify(values)
    : '';
}

function getSelectedOptionLabels(options, selectedIds) {
  return (Array.isArray(selectedIds) ? selectedIds : [])
    .map(selectedId => getOptionLabel(options, selectedId))
    .filter(Boolean)
    .join(', ');
}

function keepAvailableSelections(previousValues, options) {
  const nextValues = previousValues.filter(value =>
    options.some(option => String(option.id) === String(value))
  );
  return nextValues.length === previousValues.length ? previousValues : nextValues;
}

function getUserDisplayName(user) {
  return String(user?.full_name ?? user?.fullName ?? user?.name ?? '').trim();
}

function getGroupTypeText(group) {
  const ageRange = String(group?.age_range ?? group?.ageRange ?? '').trim();
  if (ageRange) return ageRange;

  const legacyDescription = String(group?.description ?? '').trim();
  const normalizedLegacyDescription = normalizeText(legacyDescription);
  return ['docente', 'comunidad', 'estudiante'].some(type =>
    normalizedLegacyDescription.includes(type)
  )
    ? legacyDescription
    : '';
}

function resolveFormativeProcessType(group) {
  const groupType = normalizeText(getGroupTypeText(group));
  if (groupType.includes('docente')) return 'Docentes';
  if (groupType.includes('comunidad')) return 'Comunidad';
  if (groupType.includes('estudiante')) return 'Estudiantes';

  const code = String(group?.code || '').trim().toUpperCase();
  if (/GD\d+$/.test(code)) return 'Docentes';
  if (/G\d+$/.test(code)) return 'Estudiantes';

  return '';
}

function resolveAdvisoryType(type) {
  const normalizedType = String(type || '').trim().toUpperCase();
  return COP_ADVISORY_TYPE_LABELS[normalizedType] || String(type || '').trim();
}

function buildUniqueNamedOptions(values) {
  const unique = new Map();

  values.forEach(value => {
    const name = String(value || '').trim();
    if (!name) return;

    unique.set(name, {
      id: name,
      name
    });
  });

  return sortOptionsByName(Array.from(unique.values()));
}

function buildLocationOptions(rows, getId, getName) {
  return sortOptionsByName(
    Array.from(
      rows.reduce((optionsMap, row) => {
        const id = String(getId(row) || '').trim();
        const name = String(getName(row) || '').trim();

        if (!id || !name) {
          return optionsMap;
        }

        optionsMap.set(id, {
          id,
          name
        });

        return optionsMap;
      }, new Map()).values()
    )
  );
}

function getRowSchoolCode(row) {
  return String(row?.code ?? '').trim();
}

function getRowSchoolName(row) {
  return String(row?.name ?? row?.address ?? row?.organization ?? '').trim();
}

function getRowProcessType(row) {
  return String(row?.formativeProcessType ?? '').trim();
}

function getRowGroupCode(row) {
  return String(row?.groupInfo?.code ?? '').trim();
}

function getRowAdvisorName(row) {
  return String(row?.name ?? row?.address ?? '').trim();
}

function getRowMediatorId(row) {
  return String(
    row?.groupInfo?.userId ??
      row?.groupInfo?.user_id ??
      row?.userId ??
      row?.user_id ??
      ''
  ).trim();
}

function getRowMediatorName(row) {
  return getUserDisplayName(row?.groupInfo?.user) || getUserDisplayName(row?.user);
}

function getRowMunicipalityId(row) {
  return String(
    row?.groupInfo?.municipalityId ??
      row?.groupInfo?.municipality_id ??
      row?.municipalityId ??
      row?.municipality_id ??
      ''
  ).trim();
}

function getRowMunicipalityName(row) {
  return String(
    row?.groupInfo?.municipality?.name ?? row?.municipality?.name ?? ''
  ).trim();
}

function getRowNeighborhoodId(row) {
  return String(
    row?.groupInfo?.neighborhoodId ??
      row?.groupInfo?.neighborhood_id ??
      row?.neighborhoodId ??
      row?.neighborhood_id ??
      ''
  ).trim();
}

function getRowNeighborhoodName(row) {
  return String(
    row?.groupInfo?.neighborhood?.name ?? row?.neighborhood?.name ?? ''
  ).trim();
}

function getRowStatus(row, scope = '') {
  return resolveGroupStatus(row?.groupInfo, '', {
    schoolCode: row?.code,
    scope
  });
}

function buildAdminSchoolsFiltersSearch({
  scope,
  schoolCode,
  schoolName,
  entityType,
  processType,
  groupCode,
  status,
  mediatorId,
  municipalityId,
  neighborhoodId,
  advisorName,
  dateFrom,
  dateTo,
  dateField
}) {
  const searchParams = new URLSearchParams();

  if (scope) searchParams.set('scope', scope);
  if (schoolCode) searchParams.set('schoolCode', schoolCode);
  if (schoolName) searchParams.set('schoolName', schoolName);
  if (entityType) searchParams.set('entityType', entityType);
  if (processType) searchParams.set('processType', processType);
  if (groupCode) searchParams.set('groupCode', groupCode);
  if (status) searchParams.set('status', status);
  if (advisorName) searchParams.set('advisorName', advisorName);
  if (mediatorId) searchParams.set('mediatorId', mediatorId);
  if (municipalityId) searchParams.set('municipalityId', municipalityId);
  if (neighborhoodId) searchParams.set('neighborhoodId', neighborhoodId);
  if (dateFrom) searchParams.set('dateFrom', dateFrom);
  if (dateTo) searchParams.set('dateTo', dateTo);
  if (dateField === DATE_FIELD_FINALIZATION) {
    searchParams.set('dateField', dateField);
  }

  return searchParams.toString();
}

function AdminSchools() {
  const [searchParams, setSearchParams] = useSearchParams();
  const adminScope = normalizeAdminScope(searchParams.get('scope'));
  const isCopScope = adminScope === ADMIN_SCOPE_VALUES.COP;
  const isNdnScope = adminScope === ADMIN_SCOPE_VALUES.NDN;
  const isDiplomScope = adminScope === ADMIN_SCOPE_VALUES.DIPLOM;
  const isSocialControlScope = adminScope === ADMIN_SCOPE_VALUES.CS;
  const supportsFinalizationDate = !isCopScope && !isSocialControlScope;
  const processEntityLabel = isCopScope ? 'Asesoría' : 'Proceso Formativo';
  const itemsDescription = isCopScope
    ? copItemsDescription
    : supportsFinalizationDate
      ? defaultItemsDescription
      : defaultItemsDescription.filter(item => item.code !== 'FIF');
  const [openModal, setOpenModal] = useState(false);
  const [openEditLookupModal, setOpenEditLookupModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSchoolCodes, setSelectedSchoolCodes] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolCode'))
  );
  const [selectedSchoolNames, setSelectedSchoolNames] = useState(() =>
    parseMultiFilterValue(searchParams.get('schoolName'))
  );
  const [selectedEntityTypes, setSelectedEntityTypes] = useState(() =>
    parseMultiFilterValue(searchParams.get('entityType'))
  );
  const [selectedProcessTypes, setSelectedProcessTypes] = useState(() =>
    parseMultiFilterValue(searchParams.get('processType'))
  );
  const [selectedGroupCodes, setSelectedGroupCodes] = useState(() =>
    parseMultiFilterValue(
      searchParams.get(isCopScope ? 'advisorName' : 'groupCode')
    )
  );
  const [selectedStatuses, setSelectedStatuses] = useState(() =>
    parseMultiFilterValue(searchParams.get('status'))
  );
  const [selectedMediatorIds, setSelectedMediatorIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('mediatorId'))
  );
  const [selectedMunicipalityIds, setSelectedMunicipalityIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('municipalityId'))
  );
  const [selectedNeighborhoodIds, setSelectedNeighborhoodIds] = useState(() =>
    parseMultiFilterValue(searchParams.get('neighborhoodId'))
  );
  const initialDateFilters = getDateFiltersFromSearchParams(searchParams);
  const [dateFrom, setDateFrom] = useState(initialDateFilters.dateFrom);
  const [dateTo, setDateTo] = useState(initialDateFilters.dateTo);
  const [dateField, setDateField] = useState(
    searchParams.get('dateField') === DATE_FIELD_FINALIZATION
      ? DATE_FIELD_FINALIZATION
      : DATE_FIELD_START
  );
  const [schoolCodeSort, setSchoolCodeSort] = useState({
    active: false,
    direction: 'asc'
  });

  async function onCreateSchool(created) {
    if (!created) return;

    toast('Proceso Formativo creado exitosamente!', {
      type: 'success'
    });
    await getSchoolsInfo();
  }

  async function onEditSchool(saved) {
    if (!saved) return;

    toast('Proceso Formativo actualizado exitosamente!', {
      type: 'success'
    });
    setEditingSchool(null);
    await getSchoolsInfo();
  }

  async function getSchoolsInfo() {
    const response = isSocialControlScope
      ? await getControlSocialActivities()
      : await getSchools();
    const normalizedResponse = isSocialControlScope
      ? (Array.isArray(response) ? response : []).map(activity => ({
          id: activity?.idActivity,
          code: activity?.name,
          name: activity?.line || activity?.name,
          organization: activity?.line,
          programScope: 'CS',
          active: true,
          userId: activity?.userId,
          groups: (Array.isArray(activity?.groups) ? activity.groups : []).map(
            group => ({
              ...group,
              municipality: { name: group?.comuna || '-' },
              neighborhood: { name: group?.barrio || '-' },
              organization: group?.place || group?.address || activity?.line,
              user:
                users.find(user => Number(user?.id) === Number(activity?.userId)) ||
                null
            })
          )
        }))
      : response;
    setSchools(Array.isArray(normalizedResponse) ? normalizedResponse : []);
  }

  async function getBasicInfo() {
    try {
      const [municipalitiesResponse, usersResponse] = await Promise.all([
        getMunicipalities(),
        getUsers({ noCache: true })
      ]);

      setMunicipalities(
        Array.isArray(municipalitiesResponse) ? municipalitiesResponse : []
      );
      setUsers(
        Array.isArray(usersResponse)
          ? usersResponse.map(user => ({
              id: String(user?.id ?? ''),
              name: getUserDisplayName(user),
              active: user?.active,
              roleName: String(user?.role?.name ?? user?.roleName ?? '').trim(),
              roleId: Number(user?.role?.id ?? user?.roleId ?? 0)
            }))
          : []
      );

      await getSchoolsInfo();
    } finally {
      setHasLoadedInitialData(true);
    }
  }

  useEffect(() => {
    getBasicInfo();
  }, []);

  useEffect(() => {
    if (!supportsFinalizationDate && dateField !== DATE_FIELD_START) {
      setDateField(DATE_FIELD_START);
    }
  }, [supportsFinalizationDate, dateField]);

  const scopedSchools = useMemo(
    () => filterConfirmedSchools(buildScopedSchools(schools, adminScope)),
    [schools, adminScope]
  );

  const schoolRows = useMemo(() => {
    return scopedSchools.flatMap(school => {
      const groups = Array.isArray(school?.groups) ? school.groups : [];

      if (groups.length === 0) {
      return [
        {
          ...school,
          groupInfo: null,
          formativeProcessType: isCopScope ? resolveAdvisoryType(school?.type) : ''
        }
      ];
      }

      return groups.map(group => ({
        ...school,
        groupInfo: group,
        formativeProcessType: isCopScope
          ? resolveAdvisoryType(school?.type)
          : resolveFormativeProcessType(group)
      }));
    });
  }, [scopedSchools, isCopScope]);

  const currentFilters = useMemo(
    () => ({
      selectedSchoolCodes,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedProcessTypes,
      selectedGroupCodes,
      selectedStatuses,
      selectedMediatorIds,
      selectedMunicipalityIds,
      selectedNeighborhoodIds,
      dateFrom,
      dateTo,
      dateField
    }),
    [
      selectedSchoolCodes,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedProcessTypes,
      selectedGroupCodes,
      selectedStatuses,
      selectedMediatorIds,
      selectedMunicipalityIds,
      selectedNeighborhoodIds,
      dateFrom,
      dateTo,
      dateField
    ]
  );

  function matchesSchoolRowFilters(row, excludedKeys = []) {
    const excluded = new Set(excludedKeys);

    const matchesSchoolCode =
      excluded.has('selectedSchoolCode') ||
      currentFilters.selectedSchoolCodes.length === 0 ||
      currentFilters.selectedSchoolCodes.includes(getRowSchoolCode(row));
    const matchesSchoolName =
      isCopScope ||
      excluded.has('selectedSchoolName') ||
      currentFilters.selectedSchoolNames.length === 0 ||
      currentFilters.selectedSchoolNames.some(schoolName =>
        normalizeText(getRowSchoolName(row)) === normalizeText(schoolName)
      );
    const matchesEntity =
      excluded.has('selectedEntityType') ||
      currentFilters.selectedEntityTypes.length === 0 ||
      currentFilters.selectedEntityTypes.some(entityType =>
        matchesEntityType(row, entityType)
      );
    const matchesProcessType =
      excluded.has('selectedProcessType') ||
      currentFilters.selectedProcessTypes.length === 0 ||
      currentFilters.selectedProcessTypes.some(processType =>
        normalizeText(getRowProcessType(row)) === normalizeText(processType)
      );
    const matchesGroupCode =
      excluded.has('selectedGroupCode') ||
      currentFilters.selectedGroupCodes.length === 0 ||
      currentFilters.selectedGroupCodes.some(groupCode =>
        isCopScope
          ? normalizeText(getRowAdvisorName(row)) === normalizeText(groupCode)
          : getRowGroupCode(row) === groupCode
      );
    const matchesStatus =
      isCopScope ||
      excluded.has('selectedStatus') ||
      currentFilters.selectedStatuses.length === 0 ||
      currentFilters.selectedStatuses.some(status =>
        normalizeText(getRowStatus(row, adminScope)) === normalizeText(status)
      );
    const matchesMediator =
      excluded.has('selectedMediator') ||
      currentFilters.selectedMediatorIds.length === 0 ||
      currentFilters.selectedMediatorIds.includes(getRowMediatorId(row));
    const matchesMunicipality =
      excluded.has('selectedMunicipalityId') ||
      currentFilters.selectedMunicipalityIds.length === 0 ||
      currentFilters.selectedMunicipalityIds.includes(getRowMunicipalityId(row));
    const matchesNeighborhood =
      excluded.has('selectedNeighborhoodId') ||
      currentFilters.selectedNeighborhoodIds.length === 0 ||
      currentFilters.selectedNeighborhoodIds.includes(getRowNeighborhoodId(row));
    const matchesDate =
      excluded.has('dateRange') ||
      isDateInRange(
        currentFilters.dateField === DATE_FIELD_FINALIZATION
          ? getGroupFinalizedDate(row?.groupInfo)
          : getGroupStartDate(row?.groupInfo) || getRecordCreatedDate(row),
        { dateFrom: currentFilters.dateFrom, dateTo: currentFilters.dateTo }
      );

    return (
      matchesSchoolCode &&
      matchesSchoolName &&
      matchesEntity &&
      matchesProcessType &&
      matchesGroupCode &&
      matchesStatus &&
      matchesMediator &&
      matchesMunicipality &&
      matchesNeighborhood &&
      matchesDate
    );
  }

  const allSchoolCodeOptions = useMemo(
    () => buildUniqueNamedOptions(schoolRows.map(getRowSchoolCode)),
    [schoolRows]
  );

  const schoolCodeOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        schoolRows
          .filter(row => matchesSchoolRowFilters(row, ['selectedSchoolCode']))
          .map(getRowSchoolCode)
      ),
    [schoolRows, currentFilters]
  );

  const schoolNameOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        schoolRows
          .filter(row => matchesSchoolRowFilters(row, ['selectedSchoolName']))
          .map(getRowSchoolName)
      ),
    [schoolRows, currentFilters]
  );

  const processTypeOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        schoolRows
          .filter(row => matchesSchoolRowFilters(row, ['selectedProcessType']))
          .map(getRowProcessType)
      ),
    [schoolRows, currentFilters]
  );

  const groupCodeOptions = useMemo(
    () =>
      buildUniqueNamedOptions(
        schoolRows
          .filter(row => matchesSchoolRowFilters(row, ['selectedGroupCode']))
          .map(row =>
            isCopScope ? getRowAdvisorName(row) : getRowGroupCode(row)
          )
      ),
    [schoolRows, currentFilters, isCopScope]
  );

  const mediatorOptions = useMemo(
    () =>
      buildLocationOptions(
        schoolRows.filter(row => matchesSchoolRowFilters(row, ['selectedMediator'])),
        getRowMediatorId,
        getRowMediatorName
      ),
    [schoolRows, currentFilters]
  );

  const statusOptions = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.filter(option =>
        schoolRows
          .filter(row => matchesSchoolRowFilters(row, ['selectedStatus']))
          .some(
            row => normalizeText(getRowStatus(row, adminScope)) === normalizeText(option.id)
          )
      ),
    [schoolRows, currentFilters, adminScope]
  );

  const municipalityOptions = useMemo(
    () =>
      buildLocationOptions(
        schoolRows.filter(row =>
          matchesSchoolRowFilters(row, ['selectedMunicipalityId'])
        ),
        getRowMunicipalityId,
        getRowMunicipalityName
      ),
    [schoolRows, currentFilters]
  );

  const neighborhoodOptions = useMemo(
    () =>
      buildLocationOptions(
        schoolRows.filter(row =>
          matchesSchoolRowFilters(row, ['selectedNeighborhoodId'])
        ),
        getRowNeighborhoodId,
        getRowNeighborhoodName
      ),
    [schoolRows, currentFilters]
  );

  const schoolFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los codigos' }, ...schoolCodeOptions],
    [schoolCodeOptions]
  );

  const schoolNameFilterOptions = useMemo(
    () => [
      { id: ALL_FILTER_ID, name: 'Todas las instituciones/organizaciones' },
      ...schoolNameOptions
    ],
    [schoolNameOptions]
  );

  const processTypeFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los tipos' }, ...processTypeOptions],
    [processTypeOptions]
  );

  const groupFilterOptions = useMemo(
    () => [
      {
        id: ALL_FILTER_ID,
        name: isCopScope ? 'Todas las asesorías' : 'Todos los grupos'
      },
      ...groupCodeOptions
    ],
    [groupCodeOptions, isCopScope]
  );

  const mediatorFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los mediadores' }, ...mediatorOptions],
    [mediatorOptions]
  );

  const statusFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los estados' }, ...statusOptions],
    [statusOptions]
  );

  const municipalityFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todas las comunas' }, ...municipalityOptions],
    [municipalityOptions]
  );

  const neighborhoodFilterOptions = useMemo(
    () => [{ id: ALL_FILTER_ID, name: 'Todos los barrios' }, ...neighborhoodOptions],
    [neighborhoodOptions]
  );

  const selectedSchoolCodeLabel = useMemo(
    () => getSelectedOptionLabels(schoolFilterOptions, selectedSchoolCodes),
    [schoolFilterOptions, selectedSchoolCodes]
  );
  const selectedSchoolNameLabel = useMemo(
    () => getSelectedOptionLabels(schoolNameFilterOptions, selectedSchoolNames),
    [schoolNameFilterOptions, selectedSchoolNames]
  );
  const selectedEntityTypeLabel = useMemo(
    () => selectedEntityTypes
      .map(entityType => getEntityTypeFilterLabel(entityType))
      .filter(Boolean)
      .join(', '),
    [selectedEntityTypes]
  );
  const selectedMediatorLabel = useMemo(
    () => getSelectedOptionLabels(mediatorFilterOptions, selectedMediatorIds),
    [mediatorFilterOptions, selectedMediatorIds]
  );

  const selectedProcessTypeLabel = useMemo(
    () => selectedProcessTypes
      .map(processType => getOptionLabel(processTypeFilterOptions, processType))
      .filter(Boolean)
      .join(', '),
    [processTypeFilterOptions, selectedProcessTypes]
  );

  const selectedStatusLabel = useMemo(
    () => getSelectedOptionLabels(statusFilterOptions, selectedStatuses),
    [statusFilterOptions, selectedStatuses]
  );

  const selectedMunicipalityLabel = useMemo(
    () => getSelectedOptionLabels(municipalityFilterOptions, selectedMunicipalityIds),
    [municipalityFilterOptions, selectedMunicipalityIds]
  );

  const selectedNeighborhoodLabel = useMemo(
    () => getSelectedOptionLabels(neighborhoodFilterOptions, selectedNeighborhoodIds),
    [neighborhoodFilterOptions, selectedNeighborhoodIds]
  );
  const selectedGroupCodeLabel = useMemo(
    () => getSelectedOptionLabels(groupFilterOptions, selectedGroupCodes),
    [groupFilterOptions, selectedGroupCodes]
  );

  const filteredRows = useMemo(
    () => schoolRows.filter(row => matchesSchoolRowFilters(row)),
    [schoolRows, currentFilters]
  );

  const sortedRows = useMemo(() => {
    if (!schoolCodeSort.active) return filteredRows;

    return filteredRows
      .map((row, index) => ({ row, index }))
      .sort((left, right) => {
        const leftCode = getRowSchoolCode(left.row);
        const rightCode = getRowSchoolCode(right.row);
        const leftIsEmpty = !leftCode;
        const rightIsEmpty = !rightCode;

        if (leftIsEmpty && rightIsEmpty) return left.index - right.index;
        if (leftIsEmpty) return 1;
        if (rightIsEmpty) return -1;

        const compareResult = leftCode.localeCompare(rightCode, 'es', {
          numeric: true,
          sensitivity: 'base'
        });

        return schoolCodeSort.direction === 'desc'
          ? compareResult * -1 || left.index - right.index
          : compareResult || left.index - right.index;
      })
      .map(item => item.row);
  }, [filteredRows, schoolCodeSort.active, schoolCodeSort.direction]);

  const hasActiveFilters = Boolean(
    ['pf', 'diplom', 'ndn'].includes(adminScope) ||
      selectedSchoolCodes.length > 0 ||
      (!isCopScope && selectedSchoolNames.length > 0) ||
      selectedEntityTypes.length > 0 ||
      selectedProcessTypes.length > 0 ||
      selectedGroupCodes.length > 0 ||
      (!isCopScope && selectedStatuses.length > 0) ||
      selectedMediatorIds.length > 0 ||
      selectedMunicipalityIds.length > 0 ||
      selectedNeighborhoodIds.length > 0 ||
      dateFrom ||
      dateTo
  );

  const adminSchoolsFiltersSearch = useMemo(
    () =>
      buildAdminSchoolsFiltersSearch({
        scope: adminScope,
        schoolCode: serializeMultiFilterValue(selectedSchoolCodes),
        schoolName: isCopScope ? '' : serializeMultiFilterValue(selectedSchoolNames),
        entityType: isCopScope ? '' : serializeMultiFilterValue(selectedEntityTypes),
        processType: serializeMultiFilterValue(selectedProcessTypes),
        groupCode: isCopScope ? '' : serializeMultiFilterValue(selectedGroupCodes),
        status: isCopScope ? '' : serializeMultiFilterValue(selectedStatuses),
        advisorName: isCopScope ? serializeMultiFilterValue(selectedGroupCodes) : '',
        mediatorId: serializeMultiFilterValue(selectedMediatorIds),
        municipalityId: serializeMultiFilterValue(selectedMunicipalityIds),
        neighborhoodId: serializeMultiFilterValue(selectedNeighborhoodIds),
        dateFrom,
        dateTo,
        dateField
      }),
    [
      selectedSchoolCodes,
      selectedSchoolNames,
      selectedEntityTypes,
      selectedProcessTypes,
      selectedGroupCodes,
      selectedStatuses,
      adminScope,
      isCopScope,
      selectedMediatorIds,
      selectedMunicipalityIds,
      selectedNeighborhoodIds,
      dateFrom,
      dateTo,
      dateField
    ]
  );

  const adminSchoolsBackTo = useMemo(
    () =>
      `/administrador/menu/escuelas${
        adminSchoolsFiltersSearch ? `?${adminSchoolsFiltersSearch}` : ''
      }`,
    [adminSchoolsFiltersSearch]
  );

  function normalizeSelectedId(optionId) {
    const normalizedId = String(optionId ?? '').trim();
    if (!normalizedId || normalizedId === ALL_FILTER_ID) return '';
    return normalizedId;
  }

  function normalizeSelectedIds(optionIds) {
    return Array.isArray(optionIds)
      ? optionIds.map(normalizeSelectedId).filter(Boolean)
      : [];
  }

  function handleSchoolFilter(_, optionIds) {
    setSelectedSchoolCodes(normalizeSelectedIds(optionIds));
  }

  function handleSchoolNameFilter(_, optionIds) {
    setSelectedSchoolNames(normalizeSelectedIds(optionIds));
  }

  function handleEntityTypeFilter(_, optionIds) {
    setSelectedEntityTypes(normalizeSelectedIds(optionIds));
  }

  function handleProcessTypeFilter(_, optionIds) {
    setSelectedProcessTypes(Array.isArray(optionIds) ? optionIds : []);
  }

  function handleGroupFilter(_, optionIds) {
    setSelectedGroupCodes(normalizeSelectedIds(optionIds));
  }

  function handleStatusFilter(_, optionIds) {
    setSelectedStatuses(normalizeSelectedIds(optionIds));
  }

  function handleMediatorFilter(_, optionIds) {
    setSelectedMediatorIds(normalizeSelectedIds(optionIds));
  }

  function handleMunicipalityFilter(_, optionIds) {
    setSelectedMunicipalityIds(normalizeSelectedIds(optionIds));
  }

  function handleNeighborhoodFilter(_, optionIds) {
    setSelectedNeighborhoodIds(normalizeSelectedIds(optionIds));
  }

  function handleClearFilters({ resetProcessScope = true } = {}) {
    setSelectedSchoolCodes([]);
    setSelectedSchoolNames([]);
    setSelectedEntityTypes([]);
    setSelectedProcessTypes([]);
    setSelectedGroupCodes([]);
    setSelectedStatuses([]);
    setSelectedMediatorIds([]);
    setSelectedMunicipalityIds([]);
    setSelectedNeighborhoodIds([]);
    setDateFrom('');
    setDateTo('');
    setDateField(DATE_FIELD_START);

    if (resetProcessScope && ['pf', 'diplom', 'ndn'].includes(adminScope)) {
      const nextSearchParams = new URLSearchParams();
      nextSearchParams.set('scope', ADMIN_SCOPE_VALUES.GENERAL);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }

  function handleDateFilterChange(key, value) {
    if (key === 'dateFrom') setDateFrom(value);
    if (key === 'dateTo') setDateTo(value);
  }

  function handleSchoolCodeSort() {
    setSchoolCodeSort(previousSort => ({
      active: true,
      direction:
        previousSort.active && previousSort.direction === 'asc' ? 'desc' : 'asc'
    }));
  }

  function renderSchoolCodeSortButton() {
    const nextDirection =
      schoolCodeSort.active && schoolCodeSort.direction === 'asc'
        ? 'desc'
        : 'asc';
    const directionLabel = nextDirection === 'asc' ? 'A a Z' : 'Z a A';

    return (
      <button
        type='button'
        className={`schools-sort-button${
          schoolCodeSort.active ? ' schools-sort-button--active' : ''
        }`}
        onClick={handleSchoolCodeSort}
        title={`Ordenar por codigo ${directionLabel}`}
        aria-label={`Ordenar por codigo ${directionLabel}`}
        aria-pressed={schoolCodeSort.active}
      >
        <img
          className={`schools-sort-button__icon${
            schoolCodeSort.active && schoolCodeSort.direction === 'desc'
              ? ' schools-sort-button__icon--desc'
              : ''
          }`}
          src={schoolCodeSort.active ? ArrowBlueIcon : ArrowGreyIcon}
          alt=''
        />
      </button>
    );
  }

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedSchoolCodes(previousValues =>
      keepAvailableSelections(previousValues, schoolFilterOptions)
    );
  }, [hasLoadedInitialData, schoolFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedSchoolNames(previousValues =>
      keepAvailableSelections(previousValues, schoolNameFilterOptions)
    );
  }, [hasLoadedInitialData, schoolNameFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedEntityTypes(previousValues =>
      keepAvailableSelections(previousValues, ENTITY_TYPE_FILTER_OPTIONS)
    );
  }, [hasLoadedInitialData]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedProcessTypes(previousValues =>
      keepAvailableSelections(previousValues, processTypeFilterOptions)
    );
  }, [hasLoadedInitialData, processTypeFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedGroupCodes(previousValues =>
      keepAvailableSelections(previousValues, groupFilterOptions)
    );
  }, [hasLoadedInitialData, groupFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedStatuses(previousValues =>
      keepAvailableSelections(previousValues, statusFilterOptions)
    );
  }, [hasLoadedInitialData, statusFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedMediatorIds(previousValues =>
      keepAvailableSelections(previousValues, mediatorFilterOptions)
    );
  }, [hasLoadedInitialData, mediatorFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedMunicipalityIds(previousValues =>
      keepAvailableSelections(previousValues, municipalityFilterOptions)
    );
  }, [hasLoadedInitialData, municipalityFilterOptions]);

  useEffect(() => {
    if (!hasLoadedInitialData) return;
    setSelectedNeighborhoodIds(previousValues =>
      keepAvailableSelections(previousValues, neighborhoodFilterOptions)
    );
  }, [hasLoadedInitialData, neighborhoodFilterOptions]);

  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === adminSchoolsFiltersSearch) return;

    const nextSearchParams = new URLSearchParams(adminSchoolsFiltersSearch);
    setSearchParams(nextSearchParams, { replace: true });
  }, [adminSchoolsFiltersSearch, searchParams, setSearchParams]);

  function handleOpenCreateModal() {
    setEditingSchool(null);
    setOpenEditLookupModal(false);
    setOpenModal(true);
  }

  function handleOpenEditLookupModal() {
    setOpenModal(false);
    setEditingSchool(null);
    setOpenEditLookupModal(true);
  }

  function handleEditSchoolLookup(schoolCode) {
    const normalizedCode = String(schoolCode || '').trim();

    if (!normalizedCode) {
      toast('Por favor ingresa el codigo de la IE', {
        type: 'warning'
      });
      return;
    }

    const schoolMatch = scopedSchools.find(
      school => normalizeText(school?.code) === normalizeText(normalizedCode)
    );

    if (!schoolMatch) {
      toast('No encontramos una entidad aliada con ese código', {
        type: 'warning'
      });
      return;
    }

    setOpenEditLookupModal(false);
    setEditingSchool(schoolMatch);
  }

  return (
    <>
      {openModal && !isCopScope && !isSocialControlScope && (
        <ModalContainer>
          <CreateSchool
            municipalities={municipalities}
            schoolType={schoolType}
            setOpenModal={setOpenModal}
            users={users}
            created={onCreateSchool}
            defaultProgramScope={
              isSocialControlScope
                ? 'CS'
                : isNdnScope
                  ? 'NDN'
                  : isDiplomScope
                    ? 'DIPLOM'
                    : 'PF'
            }
          />
        </ModalContainer>
      )}
      {openEditLookupModal && (
        <ModalContainer>
          <EditSchoolLookupModal
            setOpenModal={setOpenEditLookupModal}
            onSubmit={handleEditSchoolLookup}
            schoolOptions={allSchoolCodeOptions}
          />
        </ModalContainer>
      )}
      {editingSchool && !isCopScope && !isSocialControlScope && (
        <ModalContainer>
          <CreateSchool
            mode='edit'
            initialSchool={editingSchool}
            municipalities={municipalities}
            schoolType={schoolType}
            setOpenModal={() => setEditingSchool(null)}
            users={users}
            created={onEditSchool}
            defaultProgramScope={
              isSocialControlScope
                ? 'CS'
                : isNdnScope
                  ? 'NDN'
                  : isDiplomScope
                    ? 'DIPLOM'
                    : 'PF'
            }
          />
        </ModalContainer>
      )}

      <section className='admin-schools-main-container'>
        <BreadCrumb />

        <div className='filters-container'>
          <div className='filters-top'>
            <p className='title'>
              {`FILTROS DE BUSQUEDA DE ${processEntityLabel.toUpperCase()}`}
            </p>
            <div className='schools-actions'>
              <button
                type='button'
                className='filters-clear-button'
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                Limpiar filtros
              </button>
              {!isCopScope && !isSocialControlScope && (
                <>
                  <button
                    type='button'
                    onClick={handleOpenEditLookupModal}
                    className='edit-schools-button'
                  >
                    <p>EDITAR PROCESO FORMATIVO</p>
                  </button>
                  <button
                    type='button'
                    onClick={handleOpenCreateModal}
                    className='add-schools-button'
                  >
                    <div className='icon-container'>
                      <img src={HatIcon} alt='' />
                    </div>
                    <p>AGREGAR PROCESO FORMATIVO</p>
                  </button>
                </>
              )}
            </div>
          </div>
          <div className='filters-content'>
            <AdminProcessScopeFilter
              onScopeChange={() => handleClearFilters({ resetProcessScope: false })}
            />
            <div className={`filters-row${!isCopScope ? ' filters-row--process' : ''}`}>
              <CustomFilterSelect
                optionsList={schoolFilterOptions}
                placeholder={`Codigo de ${processEntityLabel}`}
                addInfo={handleSchoolFilter}
                valueEdit={selectedSchoolCodeLabel}
                multiple={true}
                selectedValues={selectedSchoolCodes}
                searchable={true}
                searchPlaceholder={'Buscar codigo'}
              />
              {!isCopScope && (
                <CustomFilterSelect
                  optionsList={schoolNameFilterOptions}
                  placeholder={'Institucion/organizacion'}
                  addInfo={handleSchoolNameFilter}
                  valueEdit={selectedSchoolNameLabel}
                  multiple={true}
                  selectedValues={selectedSchoolNames}
                  searchable={true}
                  searchPlaceholder={'Buscar institucion u organizacion'}
                />
              )}
              {!isCopScope && (
                <CustomFilterSelect
                  optionsList={ENTITY_TYPE_FILTER_OPTIONS}
                  placeholder='Tipo de entidad'
                  addInfo={handleEntityTypeFilter}
                  valueEdit={selectedEntityTypeLabel}
                  multiple={true}
                  selectedValues={selectedEntityTypes}
                />
              )}
              <CustomFilterSelect
                optionsList={processTypeFilterOptions}
                placeholder={`Tipo de ${processEntityLabel}`}
                addInfo={handleProcessTypeFilter}
                valueEdit={selectedProcessTypeLabel}
                multiple={true}
                selectedValues={selectedProcessTypes}
              />
              <CustomFilterSelect
                optionsList={groupFilterOptions}
                placeholder={isCopScope ? 'Nombre Asesoría' : 'Grupo'}
                addInfo={handleGroupFilter}
                valueEdit={selectedGroupCodeLabel}
                multiple={true}
                selectedValues={selectedGroupCodes}
                searchable={true}
                searchPlaceholder={
                  isCopScope ? 'Buscar asesoría' : 'Buscar grupo'
                }
              />
            </div>
            <div className={`filters-row${!isCopScope ? ' filters-row--status' : ''}`}>
              <CustomFilterSelect
                optionsList={mediatorFilterOptions}
                placeholder={'Mediador'}
                addInfo={handleMediatorFilter}
                valueEdit={selectedMediatorLabel}
                multiple={true}
                selectedValues={selectedMediatorIds}
                searchable={true}
                searchPlaceholder={'Buscar mediador'}
              />
              <CustomFilterSelect
                optionsList={municipalityFilterOptions}
                placeholder={'Comuna'}
                addInfo={handleMunicipalityFilter}
                valueEdit={selectedMunicipalityLabel}
                multiple={true}
                selectedValues={selectedMunicipalityIds}
              />
              <CustomFilterSelect
                key={`neighborhood-filter-${selectedMunicipalityIds.join('-') || 'all'}`}
                optionsList={neighborhoodFilterOptions}
                placeholder={'Barrio'}
                addInfo={handleNeighborhoodFilter}
                valueEdit={selectedNeighborhoodLabel}
                multiple={true}
                selectedValues={selectedNeighborhoodIds}
                searchable={true}
                searchPlaceholder={'Buscar barrio'}
              />
              {!isCopScope && (
                <CustomFilterSelect
                  optionsList={statusFilterOptions}
                  placeholder={'Estado'}
                  addInfo={handleStatusFilter}
                  valueEdit={selectedStatusLabel}
                  multiple={true}
                  selectedValues={selectedStatuses}
                />
              )}
            </div>
            <div className={`schools-date-filter${
              supportsFinalizationDate ? '' : ' schools-date-filter--single'
            }`}>
              {supportsFinalizationDate && (
                <label className='schools-date-filter__criterion'>
                  <span>Filtrar periodo por</span>
                  <select
                    value={dateField}
                    onChange={({ target }) => setDateField(target.value)}>
                    <option value={DATE_FIELD_START}>Fecha de inicio (primera sesión)</option>
                    <option value={DATE_FIELD_FINALIZATION}>Fecha de finalización</option>
                  </select>
                </label>
              )}
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={handleDateFilterChange}
                onClear={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              />
            </div>
          </div>
        </div>
      <div className='schools-main-container'>
        <div className='schools-top'>
          <div className='schools-title-actions'>
            <p className='title'>{processEntityLabel.toUpperCase()}</p>
            {renderSchoolCodeSortButton()}
          </div>
          <div className='schools-top-actions'>
            <p className='paginator-text'>
              | Mostrando <span>{filteredRows.length}</span> de{' '}
              <span>{schoolRows.length}</span> resultados |
            </p>
              <DownloadSchoolsReport
                rows={sortedRows}
                isCopScope={isCopScope}
                isSocialControlScope={isSocialControlScope}
              />
          </div>
        </div>
          <div className='info-shools-content'>
            {filteredRows.length > 0 ? (
              <SchoolItem
                itemsDescription={itemsDescription}
                itemSchool={sortedRows}
                backTo={adminSchoolsBackTo}
                getSchoolsInfo={getSchoolsInfo}
                isCopScope={isCopScope}
                isSocialControlScope={isSocialControlScope}
                showFinalizationDate={supportsFinalizationDate}
              />
            ) : (
              <p className='schools-state'>
                  {hasActiveFilters
                  ? `No hay ${isCopScope ? 'asesorias' : 'procesos formativos'} que coincidan con los filtros.`
                  : `Aun no hay ${isCopScope ? 'asesorias' : 'procesos formativos'} para mostrar.`}
              </p>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default AdminSchools;
